/* ==========================================================================
   EL BENDECIDO — ANALYTICS BRIDGE
   ==========================================================================
   Listens to the internal DOM bus (`elb:analytics`) and forwards a sanitized,
   allowlisted subset to TWO destinations:

     1. Shopify Customer Events  -> Shopify.analytics.publish('elb:<name>')
     2. GA4, via the EXISTING page tag -> gtag('event','elb_<name>', {...})

   Shopify keeps the `elb:` colon namespace. GA4 requires names of letters,
   digits and underscores only, so GA4 receives `elb_` names. The two forms are
   deliberate and must not be merged.

   WHAT THIS FILE MUST NEVER DO
   - Load a Google tag or googletagmanager.com. The page already initializes
     G-M2L52XZ5FN, GT-M3932SWZ and MC-HE40JBQ5DS.
   - Call gtag('config', ...) or send a page_view. Both already happen.
   - Create or redefine window.gtag, or create a dataLayer.
   - Send to any destination other than G-M2L52XZ5FN.
   - Read customer input, mailto/sms/tel targets, map URLs, or free text.

   NAVIGATION
   This file never calls preventDefault and never navigates programmatically,
   so a customer click always proceeds at native speed. gtag events use the
   tag's own transport (sendBeacon / keepalive), which survives unload.
   event_callback is supplied with a short timeout purely as a completion hint;
   a single-fire guard means it can never run twice, and because we never
   navigate ourselves it can never cause a double navigation.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__elbAnalyticsBridgeReady) return;
  window.__elbAnalyticsBridgeReady = true;

  var GA4_MEASUREMENT_ID = 'G-M2L52XZ5FN';

  /* ---------------------------------------------------------------------
     EVENT NAMES
     --------------------------------------------------------------------- */
  function pair(base) {
    return { shopify: 'elb:' + base, ga4: 'elb_' + base };
  }

  var EVENTS = {
    estimate_click: pair('estimate_request_click'),
    email_click: pair('email_click'),
    estimate_email_click: pair('email_click'),
    directions_click: pair('directions_click'),
    vehicle_class_select: pair('vehicle_class_select'),
    language_switch: pair('language_switch'),
    service_view: pair('service_view'),
    pricing_view: pair('pricing_view'),
    phone_click: pair('phone_click'),
    // Call and Text share the event but are separated by destination_type.
    call_click: pair('phone_click'),
    text_click: pair('phone_click'),
    priority_booking_view: pair('priority_booking_view'),
    booking_start: pair('booking_start'),
    calendly_click: pair('calendly_click')
  };

  // destination_type is derived from the internal event name, never from the
  // href, so no tel:/sms: URL can reach a payload.
  var DESTINATION_TYPE = { call_click: 'call', text_click: 'text' };

  var ALLOWED_SHOPIFY = {};
  var ALLOWED_GA4 = {};
   ['estimate_request_click','email_click','directions_click','vehicle_class_select',
     'language_switch','service_view','pricing_view','phone_click','booking_start',
     'calendly_click','priority_booking_view'].forEach(function (b) {
    ALLOWED_SHOPIFY['elb:' + b] = true;
    ALLOWED_GA4['elb_' + b] = true;
  });

  /* ---------------------------------------------------------------------
     PAYLOAD ALLOWLIST
     --------------------------------------------------------------------- */
  var FIELDS = [
    'page_path', 'page_language', 'action_location', 'destination_type',
    'vehicle_class', 'service_key', 'component_name', 'from_language', 'to_language',
    'booking_mode', 'payment_provider', 'priority_scheduling'
  ];

  var LOCATIONS = {
    header: 1, footer: 1, contact: 1, contact_estimate: 1, mobile_bar: 1,
    hero: 1, steps: 1, service_card: 1, pricing_selector: 1, booking_router: 1,
    priority_router: 1
  };

  // Legacy `placement` values still present in markup map onto the approved
  // action_location vocabulary. Anything without an approved equivalent is
  // omitted rather than sent as a wrong-but-plausible value.
  var LOCATION_ALIAS = {
    mobile_sticky: 'mobile_bar',
    home_hero: 'hero',
    home_steps: 'steps',
    home_cta: 'steps',
    contact_form: 'contact',
    wash: 'service_card', interior: 'service_card', exterior: 'service_card',
    paint: 'service_card', ceramic: 'service_card', fleet: 'service_card',
    plans: 'service_card'
  };

  var VEHICLES = {
    sedan: 'sedan',
    midsuv: 'midsize_suv', midsize_suv: 'midsize_suv',
    fullsuv: 'fullsize_suv', fullsize_suv: 'fullsize_suv',
    pickup: 'pickup'
  };

  var SERVICE_KEYS = {
    wash: 1, interior: 1, exterior: 1, complete_detail: 1,
    paint_enhancement: 1, paint_correction: 1, ceramic_coating: 1, commercial: 1,
    standard_wash: 1, premium_wash: 1, interior_maintenance: 1,
    interior_deep: 1, exterior_sealant: 1, one_step_paint: 1
  };

  var LANGS = { en: 'en', es: 'es' };
  var DESTINATIONS = { call: 1, text: 1 };
  var COMPONENTS = { pricing_selector: 1, booking_router: 1, priority_router: 1 };
  var EVENT_TYPES = { fixed_price: 1, evaluation: 1, consultation: 1 };
  var BOOKING_MODES = { private: 1, appointments_now: 1, deposit: 1 };
  var PAYMENT_PROVIDERS = { none: 1, paypal: 1, stripe: 1, shopify_native: 1 };

  /* ---------------------------------------------------------------------
     SANITIZERS
     --------------------------------------------------------------------- */
  function cleanPath(v) {
    if (typeof v !== 'string' || !v) return null;
    if (v.indexOf('://') !== -1 || v.indexOf('//') === 0) return null;
    var p = v.split('?')[0].split('#')[0];
    if (p.charAt(0) !== '/') return null;
    return p.length > 128 ? p.slice(0, 128) : p;
  }

  function cleanLang(v) {
    if (typeof v !== 'string') return null;
    var s = v.toLowerCase().slice(0, 5);
    return LANGS[s] || LANGS[s.split('-')[0]] || null;
  }

  function token(v) {
    if (typeof v !== 'string' || !v) return null;
    var s = v.toLowerCase();
    return /^[a-z0-9_]{1,40}$/.test(s) ? s : null;
  }

  function cleanLocation(v) {
    var s = token(v);
    if (!s) return null;
    if (LOCATIONS[s]) return s;
    var a = LOCATION_ALIAS[s];
    return a && LOCATIONS[a] ? a : null;
  }

  function sanitize(internalName, ga4Name, detail) {
    var d = (detail && typeof detail === 'object') ? detail : {};
    var out = {};
    var isBookingEvent = ga4Name === 'elb_booking_start' || ga4Name === 'elb_calendly_click';
    var isPriorityEvent = ga4Name === 'elb_priority_booking_view';
    var isBookingLike = isBookingEvent || isPriorityEvent;

    var path = cleanPath(d.page_path || d.path || window.location.pathname);
    if (path && !isBookingLike) out.page_path = path;

    var lang = cleanLang(d.page_language || d.language || document.documentElement.lang);
    if (lang) out.page_language = lang;

    // action_location is the analytics-facing name. `placement` is still
    // accepted from the DOM bus for backward compatibility but is never sent.
    var loc = cleanLocation(d.action_location || d.location || d.placement);
    if (loc) out.action_location = loc;

    if (ga4Name === 'elb_phone_click') {
      var dest = DESTINATION_TYPE[internalName] || d.destination_type;
      var ds = token(dest);
      if (ds && DESTINATIONS[ds]) out.destination_type = ds;
    }

    if (ga4Name === 'elb_vehicle_class_select' || ga4Name === 'elb_pricing_view' || isBookingLike) {
      var vs = token(d.vehicle_class);
      if (vs && VEHICLES[vs]) out.vehicle_class = VEHICLES[vs];
    }

    if (ga4Name === 'elb_service_view' || isBookingEvent || (isPriorityEvent && d.service_key)) {
      var ks = token(d.service_key);
      if (!ks || !SERVICE_KEYS[ks]) return null; // no valid key -> no event at all
      out.service_key = ks;
    }

    if (ga4Name === 'elb_language_switch') {
      var f = cleanLang(d.from_language);
      var t = cleanLang(d.to_language || d.target_language);
      if (f) out.from_language = f;
      if (t) out.to_language = t;
    }

    if (isBookingEvent) {
      var et = token(d.event_type);
      var bm = token(d.booking_mode);
      if (!et || !EVENT_TYPES[et] || !bm || !BOOKING_MODES[bm]) return null;
      out.booking_mode = bm;
    }

    if (isBookingLike) {
      var provider = token(d.payment_provider);
      if (provider && PAYMENT_PROVIDERS[provider]) out.payment_provider = provider;

      var priority = d.priority_scheduling;
      if (priority === true || priority === false || priority === 'true' || priority === 'false') {
        out.priority_scheduling = priority === true || priority === 'true';
      }
    }

    if (isPriorityEvent) {
      var priorityMode = token(d.booking_mode);
      if (!priorityMode || !BOOKING_MODES[priorityMode]) return null;
      if (!out.payment_provider || typeof out.priority_scheduling !== 'boolean') return null;
      out.booking_mode = priorityMode;
    }

    var cs = token(d.component_name);
    if (cs && COMPONENTS[cs] && !isBookingLike) out.component_name = cs;

    // Final gate: only allowlisted keys survive.
    var safe = {};
    for (var i = 0; i < FIELDS.length; i++) {
      if (Object.prototype.hasOwnProperty.call(out, FIELDS[i])) safe[FIELDS[i]] = out[FIELDS[i]];
    }
    return safe;
  }

  /* ---------------------------------------------------------------------
     DEDUPLICATION
     Identity includes action_location AND destination_type, so a Call
     immediately followed by a Text produces two events instead of the second
     being swallowed as a duplicate of the first.
     --------------------------------------------------------------------- */
  var recent = Object.create(null);
  var DEDUPE_MS = 700;

  function isDuplicate(name, payload) {
    var key = name + '|' + (payload.action_location || '-') + '|' +
      (payload.destination_type || '-') + '|' + (payload.service_key || '-') + '|' +
      (payload.vehicle_class || '-') + '|' + (payload.booking_mode || '-') + '|' +
      (payload.payment_provider || '-') + '|' + (payload.priority_scheduling === true ? 'priority' : '-');
    var now = Date.now();
    if (recent[key] && now - recent[key] < DEDUPE_MS) return true;
    recent[key] = now;
    if (Object.keys(recent).length > 40) {
      for (var k in recent) { if (now - recent[k] > DEDUPE_MS * 4) delete recent[k]; }
    }
    return false;
  }

  /* ---------------------------------------------------------------------
     CONSENT — Shopify Customer Privacy API
     This storefront does not attach Shopify.customerPrivacy by default
     (verified: absent after a 12s poll, no banner configured), so the API is
     requested once via Shopify.loadFeatures. Until consent is known, GA4
     forwarding is withheld.

     Shopify.analytics.publish is NEVER gated here — Shopify applies its own
     privacy handling to that path, and gating it twice would silently drop
     events Shopify itself considers permitted.
     --------------------------------------------------------------------- */
  var consentState = 'unknown'; // 'unknown' | 'allowed' | 'denied'

  function readConsent() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
        return cp.analyticsProcessingAllowed() === true ? 'allowed' : 'denied';
      }
    } catch (e) { /* fall through */ }
    return 'unknown';
  }

  function refreshConsent() {
    var s = readConsent();
    if (s !== 'unknown' && consentState === 'unknown') {
      consentState = s;
      flushPending();          // consent just resolved — release or drop the queue
    } else if (s !== 'unknown') {
      consentState = s;
    }
    return consentState;
  }

  /* Events that fire before the consent API finishes loading would otherwise
     be dropped from GA4 entirely. service_view and pricing_view fire at page
     boot, which is exactly that window — measured live: service_view reached
     Shopify but never reached GA4. They are held here and released once
     consent resolves. Bounded in size and time so nothing accumulates. */
  var pending = [];
  var PENDING_MAX = 20;
  // Measured live: Shopify.loadFeatures can attach customerPrivacy well after
  // 4s. An earlier 4s poll / 6s TTL silently discarded service_view on some
  // page loads. These windows are sized to that observation.
  var PENDING_TTL_MS = 30000;

  // Draining requires BOTH consent resolved AND the Google tag present.
  //
  // An earlier version emptied the queue as soon as consent resolved. Measured
  // live: customerPrivacy attaches at ~200-400ms while window.gtag can appear
  // later, so queued events were handed to a gtag that did not exist yet and
  // were lost. That produced the nondeterminism where service_view reached GA4
  // on some page loads and not others. Entries now stay queued until they can
  // actually be sent.
  function readyToSend() {
    return consentState === 'allowed' && typeof window.gtag === 'function';
  }

  function flushPending() {
    if (consentState === 'denied') { pending = []; return; }   // discard
    if (!readyToSend()) return;                                // keep queued
    var q = pending;
    pending = [];
    var now = Date.now();
    for (var i = 0; i < q.length; i++) {
      if (now - q[i].t <= PENDING_TTL_MS) rawSendGa4(q[i].name, q[i].payload);
    }
  }

  // Hard stop: if consent never resolves, drop the queue rather than hold it.
  setTimeout(function () { if (consentState === 'unknown') pending = []; }, PENDING_TTL_MS);

  // Extra flush triggers, so a late-resolving consent API is still caught after
  // the poll below has stopped.
  window.addEventListener('load', function () { refreshConsent(); });
  ['pointerdown', 'keydown', 'visibilitychange'].forEach(function (evt) {
    document.addEventListener(evt, function () { refreshConsent(); }, { once: true, passive: true });
  });

  (function bootstrapConsent() {
    if (refreshConsent() !== 'unknown') return;

    // loadFeatures does not always attach customerPrivacy on the first call
    // (measured live: some page loads never resolved within 9s). Retry a few
    // times rather than simply widening the timeout.
    var attempts = 0;
    function request() {
      if (refreshConsent() !== 'unknown' || attempts >= 3) return;
      attempts++;
      try {
        if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
          window.Shopify.loadFeatures(
            [{ name: 'consent-tracking-api', version: '0.1' }],
            function () { refreshConsent(); }
          );
        }
      } catch (e) { /* never throw */ }
    }
    request();
    setTimeout(request, 1500);
    setTimeout(request, 5000);

    // Poll until resolved, up to ~30s, matching the queue TTL.
    var tries = 0;
    var iv = setInterval(function () {
      refreshConsent();
      flushPending();   // retries until consent AND gtag are both ready
      var settled = (consentState === 'denied') ||
                    (consentState === 'allowed' && pending.length === 0);
      if (settled || ++tries > 300) clearInterval(iv);
    }, 100);
  })();

  document.addEventListener('visitorConsentCollected', function () { refreshConsent(); });

  /* ---------------------------------------------------------------------
     DESTINATION 1 — Shopify Customer Events
     --------------------------------------------------------------------- */
  function publishShopify(name, payload) {
    if (!ALLOWED_SHOPIFY[name]) return false;
    try {
      var a = window.Shopify && window.Shopify.analytics;
      if (!a || typeof a.publish !== 'function') return false;
      var r = a.publish(name, payload);
      if (r && typeof r.catch === 'function') r.catch(function () {});
      return true;
    } catch (e) { return false; }
  }

  /* ---------------------------------------------------------------------
     DESTINATION 2 — GA4 via the EXISTING page tag
     --------------------------------------------------------------------- */
  function sendGa4(name, payload) {
    if (!ALLOWED_GA4[name]) return false;

    var c = refreshConsent();
    if (c === 'denied') return false;
    if (c === 'unknown' || typeof window.gtag !== 'function') {
      // Consent API still loading, or the Google tag has not appeared yet.
      // Hold rather than drop; the poll below drains once both are ready.
      if (pending.length < PENDING_MAX) pending.push({ name: name, payload: payload, t: Date.now() });
      return false;
    }
    return rawSendGa4(name, payload);
  }

  function rawSendGa4(name, payload) {
    if (!ALLOWED_GA4[name]) return false;
    if (consentState !== 'allowed') return false;
    if (typeof window.gtag !== 'function') return false;   // never create one

    try {
      var params = {};
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) params[k] = payload[k];
      }
      params.send_to = GA4_MEASUREMENT_ID;

      var done = false;
      params.event_callback = function () { if (done) return; done = true; };
      params.event_timeout = 250;
      setTimeout(function () { done = true; }, 300);

      window.gtag('event', name, params);
      return true;
    } catch (e) { return false; }
  }

  /* ---------------------------------------------------------------------
     ROUTER
     --------------------------------------------------------------------- */
  function handle(internalName, detail) {
    var spec = EVENTS[internalName];
    if (!spec) return;

    var payload = sanitize(internalName, spec.ga4, detail);
    if (!payload) return;
    if (isDuplicate(spec.ga4, payload)) return;

    publishShopify(spec.shopify, payload);
    sendGa4(spec.ga4, payload);
  }

  document.addEventListener('elb:analytics', function (event) {
    var d = event && event.detail;
    if (!d || typeof d.event !== 'string') return;
    handle(d.event, d);
  });

  // Read-only QA surface. Exposes no customer data.
  window.__elbAnalytics = {
    events: EVENTS,
    consent: function () { return refreshConsent(); },
    canPublish: function () {
      return !!(window.Shopify && window.Shopify.analytics &&
                typeof window.Shopify.analytics.publish === 'function');
    },
    canGa4: function () { return typeof window.gtag === 'function'; },
    measurementId: GA4_MEASUREMENT_ID,
    sanitize: sanitize
  };
})();
